import {
  Banner,
  Box,
  BreadcrumbItem,
  ContactUsEmailLink,
  Container,
  Heading,
  Helmet,
  IconDocument,
  Layout,
  ResourcesTile,
  Row,
  Section,
  Theme,
  YouTubeVideo
} from "@life-without-barriers/react-components"

import { IGatsbyImageData } from "gatsby-plugin-image"
import React from "react"
import { Site } from "@life-without-barriers/gatsby-common"

// This is a layout that is agnostic of whether it is populated via Markdown or another source.

export interface ContentLayoutProps {
  location: { pathname: string }
  breadcrumbs: BreadcrumbItem[]
  children: React.ReactElement
  ogImage: IGatsbyImageData
  site: Site
  contentProps: ContentProps
  afterContent?: React.ReactElement
  footnoteContent?: React.ReactElement
}

interface Resource {
  name: string
  reference: {
    publicURL: string
  }
}

interface ContentProps {
  metaTitle: string
  metaDescription: string
  title: string
  contentTitle?: string
  intro?: string
  contactUsComment?: string
  contactUsEmail?: string
  resources?: Resource[]
  videoSection?: {
    youTubeVideoId: string
    title: string
    text: string
  }
}
const ContentLayout = ({
  location,
  breadcrumbs,
  contentProps: {
    metaTitle,
    metaDescription,
    title,
    contentTitle,
    intro,
    contactUsComment,
    contactUsEmail,
    resources,
    videoSection
  },
  children,
  footnoteContent,
  ogImage,
  site,
  afterContent
}: ContentLayoutProps) => {
  const theme = Theme.youthFull
  return (
    <div>
      <Helmet
        title={`${metaTitle} | ${site.siteMetadata.title}`}
        description={metaDescription}
        siteUrl={site.siteMetadata.siteUrl}
        path={location.pathname}
        image={ogImage}
      />
      <Layout theme={theme}>
        <Banner title={title} breadcrumbs={breadcrumbs} />
        {videoSection && (
          <Section background="bg-lwb-grey-xxx-light">
            <Container>
              <Row>
                <Box className="layout-readable">
                  <Heading size={2}>{videoSection.title}</Heading>
                  <p>{videoSection.text}</p>
                  <YouTubeVideo
                    className="mt4"
                    videoId={videoSection.youTubeVideoId}
                    title={videoSection.title}
                  />
                </Box>
              </Row>
            </Container>
          </Section>
        )}

        <Container>
          <Row>
            <Box className="layout-readable">
              {contentTitle && (
                <div className="mb3">
                  <Heading size={2} className="fw8 ts-display-3">
                    {contentTitle}
                  </Heading>
                </div>
              )}
              {intro && <p className="copy-lrg b mb4">{intro}</p>}
              {children}
              {resources && (
                <ResourcesTile
                  name="Resources"
                  icon={<IconDocument height="25" color={theme.medium} />}
                  resources={resources.map((r) => ({
                    ...r,
                    reference: r.reference.publicURL
                  }))}
                  className="mt5 br2 lwb-shadow"
                  styles={{
                    iconWrapperColor: theme.xxxlight,
                    itemHoverColor: theme.xxxlight
                  }}
                />
              )}
            </Box>
          </Row>
        </Container>
        {footnoteContent}

        {contactUsEmail && (
          <Section background="bg-hex-image">
            <Container>
              <Row>
                <Box>
                  <Heading size={2}>Contact us</Heading>
                  {contactUsComment && (
                    <div className="dib-ns mb2 mr3">
                      <p className="copy pt2">{contactUsComment}</p>
                    </div>
                  )}
                  <div className="flex-l mt1-l">
                    <div className="dib ml2 mr4 mt2">
                      <ContactUsEmailLink
                        marginClasses="mt0"
                        text={contactUsEmail}
                        link={`mailto:${contactUsEmail}`}
                      />
                    </div>
                  </div>
                </Box>
              </Row>
            </Container>
          </Section>
        )}
        {afterContent}
      </Layout>
    </div>
  )
}
export default ContentLayout
